// Simulation modes, in one place.
//
// Recording modes (client/session — SimulatorContext.recordingMode and the
// `mode` URL param):
//   "regular" — Practice: tap-to-speak or type, turn by turn, resources open.
//   "livemic" — Live Mic: continuous hands-free mic like a real call, but the
//               session is NOT scored and resources stay open. The middle
//               ground between Practice and Scored.
//   "vad"     — Scored: continuous hands-free mic AND the session is scored;
//               resources are locked away during the call.
//
// Result modes (persisted on *SimulationResult.mode), mapped 1:1:
//   "PRACTICE" | "LIVE_MIC" | "LIVE"
// Only "LIVE" results ever carry a totalScore. Everywhere results are split
// practice-vs-scored, "LIVE_MIC" counts as practice.

export function isLiveSimulationMode(mode) {
  return String(mode || "").toUpperCase() === "LIVE";
}

export function isLiveMicSimulationMode(mode) {
  return String(mode || "").toUpperCase() === "LIVE_MIC";
}

// Result modes that carry no score and count as practice wherever results are
// filtered practice-vs-scored (completion counts, histories, reports).
export const PRACTICE_LIKE_RESULT_MODES = ["PRACTICE", "LIVE_MIC"];

export function isPracticeLikeSimulationMode(mode) {
  return PRACTICE_LIKE_RESULT_MODES.includes(String(mode || "").toUpperCase());
}

// The one spelling of each result mode, for assignments and results alike.
// A result card used to say "Assessment" where every roleplay surface said
// "Scored"; this is where that was reconciled.
export function getSimulationModeLabel(mode) {
  if (isLiveSimulationMode(mode)) return "Scored";
  return isLiveMicSimulationMode(mode) ? "Live Mic" : "Practice";
}

// The tone hue (lib/ui/tones.js) each mode is coloured with, on every surface
// that colours a mode: the mode/type header line and the mode badge.
export function getSimulationModeHue(mode) {
  if (isLiveSimulationMode(mode)) return "action";
  return isLiveMicSimulationMode(mode) ? "premium" : "emerald";
}

export function normalizeRecordingMode(recordingMode) {
  return recordingMode === "vad" || recordingMode === "livemic"
    ? recordingMode
    : "regular";
}

// Continuous hands-free (VAD) mic mechanics — shared by Scored and Live Mic:
// auto-listening mic, caption view, no text input.
export function isVadRecordingMode(recordingMode) {
  return normalizeRecordingMode(recordingMode) !== "regular";
}

// Only Scored mode produces a scored result and locks resources mid-session.
export function isScoredRecordingMode(recordingMode) {
  return normalizeRecordingMode(recordingMode) === "vad";
}

export function getResultModeFromRecordingMode(recordingMode) {
  const normalized = normalizeRecordingMode(recordingMode);
  if (normalized === "vad") return "LIVE";
  if (normalized === "livemic") return "LIVE_MIC";
  return "PRACTICE";
}

export function getRecordingModeFromResultMode(mode) {
  if (isLiveSimulationMode(mode)) return "vad";
  if (isLiveMicSimulationMode(mode)) return "livemic";
  return "regular";
}

// ── Role-play assignment mode ────────────────────────────────────────────────
// A role-play's mode is derived, not stored directly: a non-null passingScore
// means Scored; otherwise the liveMic flag splits Live Mic from Practice.

export function getRolePlayMode(rolePlay) {
  if (rolePlay?.passingScore != null) return "LIVE";
  return rolePlay?.liveMic ? "LIVE_MIC" : "PRACTICE";
}

export function getRolePlayModeLabel(rolePlay) {
  return getSimulationModeLabel(getRolePlayMode(rolePlay));
}

// Same three-way label, but with the passing threshold appended for Scored
// role-plays — for compact meta rows ("Scored · 80%" / "Live Mic" / "Practice").
export function getRolePlayModeLabelWithScore(rolePlay) {
  if (getRolePlayMode(rolePlay) === "LIVE") {
    return `Scored · ${rolePlay?.passingScore}%`;
  }
  return getRolePlayModeLabel(rolePlay);
}

// Which simulator mode an assignment launches in (the `mode` URL param).
export function getRolePlayRecordingMode(rolePlay) {
  return getRecordingModeFromResultMode(getRolePlayMode(rolePlay));
}

function sanitizeNumericScore(totalScore) {
  return typeof totalScore === "number" && Number.isFinite(totalScore)
    ? totalScore
    : null;
}

export function sanitizeScoreForMode(mode, totalScore) {
  if (!isLiveSimulationMode(mode)) {
    return null;
  }

  return sanitizeNumericScore(totalScore);
}

export function sanitizeScoreForRecordingMode(recordingMode, totalScore) {
  if (!isScoredRecordingMode(recordingMode)) {
    return null;
  }

  return sanitizeNumericScore(totalScore);
}
